import { useCallback } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import { HateoasResponse, HateoasTarget } from '@repo/contracts';

import { executeHateoasAction } from './hateoasClient';

import { useHateoasContext } from '@/context/HateoasContext';
import myFetch from '@/utils/myFetch';

/**
 * Hook facade pour executer des actions HATEOAS dans le front.
 *
 * Responsabilites:
 * - deleguer l'appel HTTP + parse HAL au client partage
 * - synchroniser le cache global (refresh_session / clear_session)
 * - appliquer les redirects UI
 *
 * Non-responsabilite:
 * - ne resolve pas les rel vers des liens (fait par useResolveHateoasRel)
 */
export function useHateoas() {
    const router = useRouter();
    const pathname = usePathname();
    const { setScope, updateLocalActions, clearCache, loadGlobalActions } = useHateoasContext();

    const handleUpdateScope = useCallback((payload: { actions?: Record<string, HateoasTarget>; links?: Record<string, HateoasTarget> }, action: HateoasTarget) => {
        // Extraction du module qui précède "/api" (ex: "dts/api" -> "dts", "/api/v1/outbound/api" -> "outbound")
        const match = action.href.match(/(?:^|\/)([^/]+)\/api\/?(?:\?.*)?$/);
        const scopeKey = match ? match[1] : undefined;

        if (scopeKey) {
            setScope(scopeKey, payload, { sourceEndpoint: action.href });
        } else {
            updateLocalActions(payload.actions, payload.links);
        }
    }, [setScope, updateLocalActions]);

    /**
     * Effectue un appel à un endpoint API renvoyant une réponse HATEOAS.
     * Execute les actions UI contenues dans la réponse et met à jour les actions disponibles dans la session.
     * @param link action HATEOAS contenant le lien de l'endpoint.
     * @param body
     * @param pathParams paramètres si la route fournie par l'API n'est pas déjà résolue.
     * @param returnHeaders gère le cas des flux de fichiers.
     * @returns La ressource contenue dans la réponse HATEOAS.
     */
    const fetchLink = useCallback(
        async <T = unknown>(link: HateoasTarget, body?: unknown, pathParams?: Record<string, string>, returnHeaders: boolean = false): Promise<T> => {
            return executeHateoasAction<T>({
                action: link,
                request: (href, options, withHeaders) => myFetch.request(href, options, withHeaders),
                body,
                pathParams,
                returnHeaders,
                currentPathname: pathname,
                onUpdateScope: handleUpdateScope,
                onRefreshSession: async () => {
                    clearCache();
                    await loadGlobalActions();
                },
                onRedirect: (target) => router.push(target),
            });
        }, [router, pathname, handleUpdateScope, clearCache, loadGlobalActions],
    );

    const fetchLinkEnvelope = useCallback(
        async (link: HateoasTarget, body?: unknown, pathParams?: Record<string, string>): Promise<HateoasResponse | null> => {
            const response = await executeHateoasAction({
                action: link,
                request: (href, options, withHeaders) => myFetch.request(href, options, withHeaders),
                body,
                pathParams,
                currentPathname: pathname,
                onUpdateScope: handleUpdateScope,
                onRefreshSession: async () => {
                    clearCache();
                    await loadGlobalActions();
                },
                onRedirect: (target) => router.push(target),
                mode: 'envelope',
            });

            if (!response || typeof response !== 'object') {
                return null;
            }

            return response as HateoasResponse;
        }, [router, pathname, handleUpdateScope, clearCache, loadGlobalActions],
    );

    return { fetchLink, fetchLinkEnvelope };
}